import { useCallback, useEffect, useState } from "react";
import type { PopupPageDto } from "@/types/api.types";

const fetchPopupDto = async (
  popupId: string | number,
  apiUrl: string,
): Promise<PopupPageDto> => {
  const baseUrl = apiUrl.endsWith("/") ? apiUrl : `${apiUrl}/`;
  const fullUrl = `${baseUrl}api/v2/upsell-page-dto/${popupId}`;

  const response = await fetch(fullUrl, {
    credentials: "same-origin",
    headers: {
      "X-CC-CSRF-Token": window.ccRestAccess?.csrfToken ?? "",
    },
  });

  if (!response.ok) {
    throw new Error(`Server returned ${response.status} for ${fullUrl}`);
  }

  return (await response.json()) as PopupPageDto;
};

let preloadedPopupPromise: Promise<PopupPageDto> | null = null;
let initialPopupId: string | number | null = null;

if (typeof window !== "undefined") {
  initialPopupId = window.postId;

  if (initialPopupId && window.apiUrl) {
    preloadedPopupPromise = fetchPopupDto(initialPopupId, window.apiUrl);
  }
}

export function useOptOutPopupData() {
  const [data, setData] = useState<PopupPageDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (forceRefetch = false) => {
    setIsLoading(true);
    setError(null);

    try {
      if (
        !forceRefetch &&
        preloadedPopupPromise &&
        String(window.postId) === String(initialPopupId)
      ) {
        setData(await preloadedPopupPromise);
      } else {
        setData(await fetchPopupDto(window.postId, window.apiUrl));
      }
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Failed to load popup data.",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  return {
    data,
    isLoading,
    error,
    refetch: () => loadData(true),
  };
}
